/**
 * Type-safe template data for transaction confirmation modal
 */

import type { ProjectInfo, RiskAssessment, Transaction } from '../../lib/types'
import type { TemplateData } from '../template-engine'

// ERC20 approve function selector: bytes4(keccak256("approve(address,uint256)"))
const ERC20_APPROVE_SELECTOR = '0x095ea7b3'

// Smart Account execute selectors
// execute(address dest, uint256 value, bytes calldata func) = 0xb61d27f6
const EXECUTE_SELECTOR = '0xb61d27f6'

// Default explorer URL
const DEFAULT_EXPLORER_URL = 'https://beam-explorer.lumia.org'

export interface ConfirmTxTemplateData extends TemplateData {
  // Metadata
  displayName: string
  displayLogo: string

  // User information
  userAvatar: string
  userName: string

  // Domain verification
  origin: string
  domainStatusClass: string
  domainStatusText: string

  // Transaction addresses
  fromAddress: string
  toAddress: string
  fromAddressFull: string
  toAddressFull: string
  fromAddressExplorerUrl: string
  toAddressExplorerUrl: string

  // Transaction value
  hasValue: boolean
  formattedValue: string

  // Contract interaction
  showContractInteraction: boolean
  actionDescription: string

  // Gas & sponsorship
  estimatedCost: string
  isSponsored: boolean

  // Risk assessment
  showHighRisk: boolean
  riskLevel: string
  riskReasons: string
}

function formatAddress(address: string): string {
  if (address.length < 10) return address
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

function getAddressExplorerUrl(address: string, explorerUrl: string): string {
  if (!address) return ''
  const baseUrl = explorerUrl.replace(/\/$/, '')
  return `${baseUrl}/address/${address}`
}

interface ERC20ApproveData {
  spender: string
  amount: bigint
}

/**
 * Extract inner calldata from Smart Account execute() wrapper
 * execute(address dest, uint256 value, bytes calldata func)
 * Layout: selector(4) + dest(32) + value(32) + offset(32) + length(32) + func(dynamic)
 *
 * Returns:
 * - inner calldata string if execute() with non-empty func
 * - '0x' if execute() with empty func (simple transfer)
 * - null if not an execute() call
 */
function extractInnerCallData(callData: string): string | null {
  if (!callData || callData.length < 10) return null

  const selector = callData.slice(0, 10).toLowerCase()
  if (selector !== EXECUTE_SELECTOR) return null

  try {
    // Skip: selector(4 bytes = 8 hex + 2 for 0x) + dest(32 bytes = 64 hex) + value(32 bytes = 64 hex) + offset(32 bytes = 64 hex)
    // = 10 + 64 + 64 + 64 = 202
    // Then read length (32 bytes = 64 hex)
    const lengthHex = callData.slice(202, 266)
    const length = parseInt(lengthHex, 16)

    // Return '0x' for empty inner data (simple transfer via execute)
    if (length === 0) return '0x'

    // Inner calldata starts at position 266 (after length)
    const innerData = '0x' + callData.slice(266, 266 + length * 2)
    return innerData
  } catch {
    return null
  }
}

function parseERC20Approve(callData: string): ERC20ApproveData | null {
  if (!callData || callData.length < 10) return null

  let dataToCheck = callData

  // First, try to extract inner calldata if this is an execute() wrapper
  const innerData = extractInnerCallData(callData)
  if (innerData) {
    dataToCheck = innerData
  }

  const selector = dataToCheck.slice(0, 10).toLowerCase()
  if (selector !== ERC20_APPROVE_SELECTOR) return null

  // callData format: 0x095ea7b3 + 32 bytes spender + 32 bytes amount
  // Total: 4 + 64 + 64 = 132 hex chars (with 0x prefix: 138)
  if (dataToCheck.length < 138) return null

  try {
    const spender = '0x' + dataToCheck.slice(34, 74) // bytes 10-74 (skip selector + 12 zero bytes padding)
    const amountHex = dataToCheck.slice(74, 138)
    const amount = BigInt('0x' + amountHex)

    return { spender, amount }
  } catch {
    return null
  }
}

function formatApproveAmount(amount: bigint): string {
  // Max uint256 is considered "unlimited"
  const MAX_UINT256 = 2n ** 256n - 1n
  const NEAR_MAX = 2n ** 255n // Anything above this is "unlimited"

  if (amount >= NEAR_MAX) {
    return 'Unlimited'
  }

  // Format with 18 decimals (most common for ERC20)
  const tokens = Number(amount) / 1e18

  if (tokens >= 1e9) {
    return `${(tokens / 1e9).toFixed(2)}B`
  } else if (tokens >= 1e6) {
    return `${(tokens / 1e6).toFixed(2)}M`
  } else if (tokens >= 1e3) {
    return `${(tokens / 1e3).toFixed(2)}K`
  } else if (tokens >= 1) {
    return tokens.toFixed(4).replace(/\.?0+$/, '')
  } else if (tokens > 0) {
    return tokens.toFixed(8).replace(/\.?0+$/, '')
  }

  return '0'
}

/**
 * Build template data for transaction confirmation modal
 */
export function buildConfirmTxTemplateData(
  project: ProjectInfo,
  origin: string,
  transaction: Transaction,
  risk: RiskAssessment,
  metadata: { name?: string; logo?: string } | null,
  userProfile?: { avatar?: string | null; displayName?: string | null },
  explorerUrl?: string
): ConfirmTxTemplateData {
  const isVerifiedOrigin = project.domains.includes(origin)
  const isUserOp = !!transaction.userOpDetails
  const isUnknownOperation = transaction.decoded?.kind === 'unknown'

  // Use metadata if available, fallback to project info
  const displayName = metadata?.name || project.name
  const displayLogo = metadata?.logo || project.logoUrl || ''

  // User avatar/logo for right side
  const userAvatar = userProfile?.avatar || ''
  const userName = userProfile?.displayName || 'embarkAI Wallet'

  // Domain verification
  const domainStatusClass = isVerifiedOrigin ? 'verified' : 'unverified'
  const domainStatusText = isVerifiedOrigin ? '✅' : '⚠️'

  const batchOperations = transaction.userOpDetails?.operations || []
  const isBatchUserOp = isUserOp && batchOperations.length > 0

  // Transaction addresses (full and shortened)
  const fromAddressFull = transaction.userOpDetails?.sender || ''
  const toAddressFull = transaction.userOpDetails?.callTarget || batchOperations[0]?.to || transaction.to || ''
  const fromAddress = formatAddress(fromAddressFull)
  const toAddress = formatAddress(toAddressFull)

  // Explorer URLs for addresses
  const baseExplorerUrl = explorerUrl || DEFAULT_EXPLORER_URL
  const fromAddressExplorerUrl = getAddressExplorerUrl(fromAddressFull, baseExplorerUrl)
  const toAddressExplorerUrl = getAddressExplorerUrl(toAddressFull, baseExplorerUrl)

  // Format transaction value (convert from wei to readable format)
  const formatValue = (valueWei: string): string => {
    try {
      const value = BigInt(valueWei)
      if (value === 0n) return '0'

      // Convert wei to tokens (18 decimals)
      const tokens = Number(value) / 1e18

      // Format with appropriate precision
      if (tokens >= 1) {
        return tokens.toFixed(4).replace(/\.?0+$/, '') // Remove trailing zeros
      } else {
        return tokens.toFixed(8).replace(/\.?0+$/, '')
      }
    } catch {
      return '0'
    }
  }

  const transactionValue = isBatchUserOp
    ? batchOperations.reduce((total, operation) => total + BigInt(operation.value || '0'), 0n).toString()
    : transaction.value || '0'
  const formattedValue = formatValue(transactionValue)
  const hasValue = BigInt(transactionValue) > 0n

  // Contract interaction detection
  const callData = transaction.userOpDetails?.callData || transaction.data || ''

  // For UserOp, extract inner calldata from execute() wrapper to check if it's a real contract call
  const innerCallData = extractInnerCallData(callData)
  const effectiveCallData = innerCallData || callData

  // Show contract interaction only if there's actual contract call data (not just execute wrapper with empty data)
  const hasContractCall = !!effectiveCallData && effectiveCallData !== '0x' && effectiveCallData.length > 2
  const showContractInteraction =
    isUnknownOperation || isBatchUserOp || (isUserOp ? hasContractCall : !!callData && callData !== '0x')

  // Detect ERC20 approve/revoke and build action description
  let actionDescription = 'Contract Interaction'
  if (isUnknownOperation && transaction.decoded?.kind === 'unknown') {
    actionDescription = `Unknown operation (selector ${transaction.decoded.selector})`
  } else if (isBatchUserOp) {
    actionDescription = `Batch transaction (${batchOperations.length} operations)`
  } else if (showContractInteraction) {
    const approveData = parseERC20Approve(callData)
    if (approveData) {
      if (approveData.amount === 0n) {
        actionDescription = 'Revoke token approval'
      } else {
        const formattedAmount = formatApproveAmount(approveData.amount)
        actionDescription = `Approve ${formattedAmount} tokens`
      }
    }
  }

  // Gas & sponsorship
  const isSponsored = isUserOp && !!transaction.userOpDetails?.paymaster
  const estimatedCost = isSponsored ? '0 (Sponsored)' : 'Free (Sponsored Transaction)'

  // Risk assessment
  const showHighRisk = risk.level !== 'LOW'
  const riskLevel = risk.level
  const riskReasons = risk.reasons.map((r) => `<div>• ${r}</div>`).join('')

  return {
    displayName,
    displayLogo,
    userAvatar,
    userName,
    origin,
    domainStatusClass,
    domainStatusText,
    fromAddress,
    toAddress,
    fromAddressFull,
    toAddressFull,
    fromAddressExplorerUrl,
    toAddressExplorerUrl,
    hasValue,
    formattedValue,
    showContractInteraction,
    actionDescription,
    estimatedCost,
    isSponsored,
    showHighRisk,
    riskLevel,
    riskReasons
  }
}
